import * as render from './apps-render.js';

const API_BASE = '/api';

// Service status tracking
let apiErrorCount = 0;
let hasServiceInterruption = false;

export function getServiceStatus() {
    return { hasError: hasServiceInterruption, errorCount: apiErrorCount };
}

export function resetServiceStatus() {
    apiErrorCount = 0;
    hasServiceInterruption = false;
}

// Get current user ID (0 if not logged in)
function getCurrentUserId() {
    return window.currentUserId || 0;
}

// Batch update multiple icons
export async function updateIcons(icons) {
    if (!icons || icons.length === 0) return true;
    
    // Update local state immediately
    icons.forEach(icon => {
        const index = window.icons.findIndex(ic => ic.id === icon.id);
        if (index >= 0) {
            window.icons[index] = { ...icon };
        }
    });
    
    try {
        const kuser = getCurrentUserId();
        console.log(`PUT ${API_BASE}/apps/icons`, { kuser, icons: icons.map(ic => ({
            id: ic.id,
            kfolder: ic.kfolder,
            region: ic.region,
            position: ic.position,
            title: ic.title,
            type: ic.type,
            showBadge: ic.showBadge
        }))});
        
        const response = await fetch(`${API_BASE}/apps/icons`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                kuser, 
                icons: icons.map(ic => ({
                    id: ic.id,
                    kfolder: ic.kfolder,
                    region: ic.region,
                    position: ic.position,
                    title: ic.title,
                    type: ic.type,
                    showBadge: ic.showBadge
                }))
            })
        });
        if (!response.ok) {
            apiErrorCount++;
            hasServiceInterruption = true;
            return false;
        }
        
        return true;
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error batch updating icons:', error);
        return false;
    }
}

// Single icon update (calls batch internally)
export async function updateIcon(icon) {
    return await updateIcons([icon]);
}

export async function createFolder(folderData) {
    try {
        const kuser = getCurrentUserId();
        console.log(`POST ${API_BASE}/apps/folders`, { 
            kuser, 
            optimistic_folder_id: folderData.optimistic_folder_id,
            region: folderData.region,
            position: folderData.position,
            title: folderData.title,
            icon1_id: folderData.icon1_id,
            icon2_id: folderData.icon2_id
        });
        
        const response = await fetch(`${API_BASE}/apps/folders`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                kuser,
                optimistic_folder_id: folderData.optimistic_folder_id,
                region: folderData.region,
                position: folderData.position,
                title: folderData.title,
                icon1_id: folderData.icon1_id,
                icon2_id: folderData.icon2_id
            })
        });
        
        if (!response.ok) {
            apiErrorCount++;
            hasServiceInterruption = true;
            return { success: false, folder_id: null };
        }
        
        const data = await response.json();
        return { 
            success: true, 
            folder_id: data.folder_id,
            optimistic_folder_id: data.optimistic_folder_id
        };
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error creating folder:', error);
        return { success: false, folder_id: null };
    }
}

export async function deleteFolder(folderId) {
    try {
        const kuser = getCurrentUserId();
        console.log(`DELETE ${API_BASE}/apps/icons/${folderId}?kuser=${kuser}`);
        
        const response = await fetch(`${API_BASE}/apps/icons/${folderId}?kuser=${kuser}`, {
            method: 'DELETE',
            credentials: 'include'
        });
        if (!response.ok) {
            apiErrorCount++;
            hasServiceInterruption = true;
            return false;
        }
        
        return true;
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error deleting folder:', error);
        return false;
    }
}

export async function deleteIcon(iconId) {
    try {
        const kuser = getCurrentUserId();
        console.log(`DELETE ${API_BASE}/apps/icons/${iconId}?kuser=${kuser}`);
        
        const response = await fetch(`${API_BASE}/apps/icons/${iconId}?kuser=${kuser}`, {
            method: 'DELETE',
            credentials: 'include'
        });
        if (!response.ok) {
            apiErrorCount++;
            hasServiceInterruption = true;
            return false;
        }
        
        return true;
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error deleting icon:', error);
        return false;
    }
}

// API functions for region management
// Both return: { success: bool, total_regions: int, total_icons: int }
export async function insertRegions(at, count, kfolder = 0) {
    try {
        const kuser = getCurrentUserId();
        console.log(`POST ${API_BASE}/apps/insert_regions`, { kuser, kfolder, at, count });
        
        const response = await fetch(`${API_BASE}/apps/insert_regions`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kuser, kfolder, at, count })
        });
        if (!response.ok) {
            apiErrorCount++;
            hasServiceInterruption = true;
            return { success: false, total_regions: 0, total_icons: 0 };
        }
        const data = await response.json();
        return data;
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error inserting regions:', error);
        return { success: false, total_regions: 0, total_icons: 0 };
    }
}

export async function deleteRegions(at, count, kfolder = 0) {
    try {
        const kuser = getCurrentUserId();
        console.log(`POST ${API_BASE}/apps/delete_regions`, { kuser, kfolder, at, count });
        
        const response = await fetch(`${API_BASE}/apps/delete_regions`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kuser, kfolder, at, count })
        });
        if (!response.ok) {
            if (kuser == 0) {
                return { success: true };
            } else {
                apiErrorCount++;
                hasServiceInterruption = true;
                return { success: false, total_regions: 0, total_icons: 0 };
            }
        }
        const data = await response.json();
        return data;
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error deleting regions:', error);
        return { success: false, total_regions: 0, total_icons: 0 };
    }
}

// Initialize
export async function loadUserInfo() {
    try {
        const response = await fetch(`${API_BASE}/account/info`, {
            credentials: 'include'
        });
        
        if (response.ok) {
            const data = await response.json();
            window.currentUserId = data.user.id; // Store user ID
            
            document.getElementById('headerUsername').textContent = 
                data.user.display_name || data.user.username;
            
            document.querySelectorAll('.logged-in-content').forEach(el => {
                el.style.display = '';
            });
            document.querySelectorAll('.logged-out-content').forEach(el => {
                el.style.display = 'none';
            });
            
            initNavigation('apps');

            await loadIcons();
        } else {
            document.querySelectorAll('.logged-out-content').forEach(el => {
                el.style.display = '';
            });
            
            // Not logged in - show logged out state BUT still load mock icons for development
            showLoggedOutState();
            console.log('Not logged in - loading mock data for development/testing');
            window.currentUserId = 0; // Set to 0 for not logged in
            document.querySelector('.appspace-container').style.display = '';
            initNavigation('apps');
            await loadIcons();
        }
    } catch (error) {
        console.error('Error loading user info:', error);
        // On error, assume development mode and show mock data
        showLoggedOutState();
        console.log('API error - loading mock data for development/testing');
        window.currentUserId = 0; // Set to 0 for not logged in
        document.querySelector('.appspace-container').style.display = '';
        initNavigation('apps');
        await loadIcons();
    }
}

export function showLoggedOutState() {
    document.querySelectorAll('.logged-out-content').forEach(el => {
        el.style.display = '';
    });
    document.querySelectorAll('.logged-in-content').forEach(el => {
        el.style.display = 'none';
    });
}

window.logout = async function () {
    try {
        await fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            credentials: 'include'
        });
        window.location.href = '/login';
    } catch (error) {
        console.error('Logout failed:', error);
    }
};

// Load icons
export async function loadIcons() {
    try {
        const kuser = getCurrentUserId();
        console.log(`GET ${API_BASE}/apps/icons?kuser=${kuser}`);
        
        const response = await fetch(`${API_BASE}/apps/icons?kuser=${kuser}`, { 
            credentials: 'include' 
        });
        if (response.ok) {
            window.icons = await response.json();
            // Process newly installed icons (region -1)
            await processNewlyInstalledIcons();
        } else {
            // Fallback to mock data if API fails
            console.warn('Failed to load icons from API, using mock data');
            window.icons = generateMockIcons();
        }
        
        render.renderLaunchpad();
        render.navigateToFirstNonEmpty();
    } catch (error) {
        apiErrorCount++;
        hasServiceInterruption = true;
        console.error('Error loading icons:', error);
        // Fallback to mock data on error
        window.icons = generateMockIcons();
        render.renderLaunchpad();
        render.navigateToFirstNonEmpty();
    }
}

async function processNewlyInstalledIcons() {
    const { findFirstAvailablePosition } = await import('./apps-icons.js');
    
    // Group by context (kfolder)
    const contexts = {};
    window.icons.forEach(icon => {
        if (icon.region === -1) {
            if (!contexts[icon.kfolder]) {
                contexts[icon.kfolder] = [];
            }
            contexts[icon.kfolder].push(icon);
        }
    });
    
    // Process each context
    for (const kfolder in contexts) {
        const newIcons = contexts[kfolder].sort((a, b) => a.position - b.position);
        const iconsToUpdate = [];
        
        for (const icon of newIcons) {
            const pos = findFirstAvailablePosition(parseInt(kfolder));
            icon.region = pos.region;
            icon.position = pos.position;
            iconsToUpdate.push(icon);
        }
        
        if (iconsToUpdate.length > 0) {
            await updateIcons(iconsToUpdate);
        }
    }
}

export function generateMockIcons() {
    const mockIcons = [];
    const iconNames = [
        'About Zebby.org', 'Calculator', 'Cashier Crown'
    ];
    
    for (let i = 0; i < iconNames.length; i++) {
        mockIcons.push({
            id: i + 1,
            kfolder: 0,
            region: Math.floor(i / 12),
            position: i % 12,
            title: iconNames[i] || `App ${i + 1}`,
            type: 'app',
            image: i === 0 ?
                 'https://zebby.org/api/static/images/about-zebby.svg' :
                 i === 1 ? 'https://zebby.org/calculator/calculator.svg' :
                 i === 2 ? 'https://zebby.org/cashiercrown/cashier-crown.svg' : '',
            badge: i === 0 ? window.localStorage.getItem('zebbyAbout') === null ? 1 : 0 : null,
            showBadge: true,
            url: i === 0 ? 'https://zebby.org/about' :
                i === 1 ? 'https://zebby.org/calculator' :
                i === 2 ? 'https://zebby.org/cashiercrown' :
                '#app-' + (i + 1)
        });
    }
 
 /*
     // Add folders
    mockIcons.push({
        id: 100,
        kfolder: 0,
        region: 0,
        position: 6,
        title: 'Productivity',
        type: 'folder'
    });

    mockIcons.push({
        id: 101,
        kfolder: 0,
        region: 0,
        position: 7,
        title: 'Games',
        type: 'folder'
    });

    // Add icons inside Productivity folder
    const prodApps = ['Notes', 'Kanban', 'Storm'];
    prodApps.forEach((name, i) => {
        mockIcons.push({
            id: 200 + i,
            kfolder: 100,
            region: 0,
            position: i,
            title: name,
            type: 'app',
            image: null,
            badge: null,
            showBadge: false,
            url: '#' + name.toLowerCase()
        });
    });

    // Add icons inside Games folder
    const games = ['Sokoban', 'Solitaire', 'SoloMahjong', 'Slide', 'Jigsaw', 'Artillery', 'Secret Agent', 'Lemonade Stand'];
    games.forEach((name, i) => {
        mockIcons.push({
            id: 300 + i,
            kfolder: 101,
            region: 0,
            position: i,
            title: name,
            type: 'app',
            image: null,
            badge: null,
            showBadge: false,
            url: '#' + name.toLowerCase()
        });
    });
*/
    
    return mockIcons;
}

export async function checkAndDeleteEmptyFolder(folderId) {
    const folderIcons = window.icons.filter(icon => icon.kfolder === folderId);
    
    if (folderIcons.length === 0) {
        // Folder is empty, delete it
        const folderIndex = window.icons.findIndex(icon => icon.id === folderId);
        if (folderIndex >= 0) {
            window.icons.splice(folderIndex, 1);
            await deleteFolder(folderId);
        }
        
        return true;
    }
    
    return false;
}
